import dayjs from './dayjsConfig';
import ICAL from 'ical.js';

import { AVAILABILITY_TYPES } from './constants/availability.constants';

/**
 * Normalizes and wraps an iCalendar string in VCALENDAR if necessary
 * @param {string} icsString - iCalendar string (VEVENT or complete VCALENDAR)
 * @returns {string} - Normalized string wrapped in VCALENDAR if necessary
 */
const normalizeAndWrapICS = (icsString) => {
  // First normalize line breaks and spaces
  const normalized = icsString
    .replace(/\\n/g, '\n')
    .replace(/(?:\n|\s)(?=BEGIN:|UID:|SUMMARY:|DTSTART|DTEND|RRULE|DTSTAMP|END:)/g, '\n');

  // Check if it is already wrapped in VCALENDAR
  if (normalized.includes('BEGIN:VCALENDAR')) {
    return normalized;
  }

  // Make sure it contains a VEVENT
  if (!normalized.includes('BEGIN:VEVENT')) {
    console.warn('The string does not contain BEGIN:VEVENT:', normalized);
    return null;
  }

  // Wrap in VCALENDAR
  return `BEGIN:VCALENDAR\nVERSION:2.0\n${normalized.trim()}\nEND:VCALENDAR`;
};

// Helper function to extract repeat days
const extractRepeatDays = (vevent) => {
  const rruleProp = vevent.getFirstProperty('rrule');
  if (!rruleProp) return [];

  const rruleValue = rruleProp.getFirstValue();

  // Try to extract repeat days
  if (rruleValue && rruleValue.parts && rruleValue.parts.BYDAY) {
    return rruleValue.parts.BYDAY;
  }

  // Alternative: extract from RRULE string
  const rruleString = rruleProp.toICALString();
  const bydayMatch = rruleString.match(/BYDAY=([^;]+)/i);
  if (bydayMatch && bydayMatch[1]) {
    return bydayMatch[1].split(',');
  }

  return [];
};

// Base function to read iCalendar events
const readBaseEvent = (icsString) => {
  const wrapped = normalizeAndWrapICS(icsString);
  if (!wrapped) return null;

  try {
    const jcalData = ICAL.parse(wrapped);
    const component = new ICAL.Component(jcalData);
    const vevent = component.getFirstSubcomponent('vevent');

    if (!vevent) {
      console.warn('No VEVENT found');
      return null;
    }

    return { vevent, event: new ICAL.Event(vevent) };
  } catch (error) {
    console.error('Error parsing iCalendar event:', error);
    return null;
  }
};

export const readICalendarRule = (rule, type) => {
  const baseEvent = readBaseEvent(rule);
  if (!baseEvent) return null;

  const { vevent, event } = baseEvent;

  switch (type) {
    case AVAILABILITY_TYPES.SINGLE_EVENT: // 🔵 Case 1: it only happens once with date and time
      return readSingleDayEvent(event);

    case AVAILABILITY_TYPES.RECURRING: // 🟢 Case 2: what happens on repeated days
      return readRecurringEvent(event, vevent);

    case AVAILABILITY_TYPES.FULL_DAYS: // 🟡 Case 3: what happens on a full day
      return readFullDayEvent(event, vevent);

    case AVAILABILITY_TYPES.RECURRING_FULL_DAYS: // 🟣 Case 4: what happens on full days and repeats
      return readRecurringFullDayEvent(event, vevent);

    default:
      return null;
  }
};

const readSingleDayEvent = (event) => {
  if (!event.startDate) {
    console.warn('VEVENT does not have startDate');
    return null;
  }

  const start = event.startDate.toJSDate();
  const end = event.endDate?.toJSDate?.() || null;

  return {
    title: event.summary || '',
    startDate: dayjs(start).format('YYYY-MM-DD'),
    startTime: dayjs(start).format('HH:mm'),
    startTimeDate: dayjs(start).format('YYYY-MM-DD HH:mm'),
    endDate: end ? dayjs(end).format('YYYY-MM-DD') : null,
    endTime: end ? dayjs(end).format('HH:mm') : null,
    endTimeDate: end ? dayjs(end).format('YYYY-MM-DD HH:mm') : null,
    recurring: false,
    fullDays: false,
  };
};

const readRecurringEvent = (event, vevent) => {
  const start = event.startDate?.toJSDate();
  const end = event.endDate?.toJSDate();

  const repeatDays = extractRepeatDays(vevent);

  if (!start || !end || repeatDays.length === 0) {
    console.warn('Incomplete recurring event');
    return null;
  }

  return {
    startTime: dayjs(start).format('HH:mm'),
    startTimeDate: dayjs(start).format('YYYY-MM-DD HH:mm'),
    endTime: dayjs(end).format('HH:mm'),
    endTimeDate: dayjs(end).format('YYYY-MM-DD HH:mm'),
    days: repeatDays,
    recurring: true,
    fullDays: false,
  };
};

const readFullDayEvent = (event, vevent) => {
  const startDate = event.startDate?.toJSDate();
  if (!startDate) {
    console.warn('Missing DTSTART');
    return null;
  }

  // Check if there is an RRULE rule
  const hasRrule = vevent.hasProperty('rrule');

  // Get end date if exists
  let endDate = null;
  if (event.endDate) {
    endDate = event.endDate.toJSDate();
    // DTEND is exclusive in iCalendar, subtract one day to get the inclusive end date
    endDate = dayjs(endDate).subtract(1, 'day').toDate();
  }

  return {
    startDate: dayjs(startDate).format('YYYY-MM-DD'),
    endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
    recurring: hasRrule,
    fullDays: true,
  };
};

const readRecurringFullDayEvent = (event, vevent) => {
  const startDate = event.startDate?.toJSDate();
  if (!startDate) {
    console.warn('Missing DTSTART');
    return null;
  }

  // Get end date if exists
  let endDate = null;
  if (event.endDate) {
    endDate = event.endDate.toJSDate();
    // DTEND is exclusive in iCalendar, subtract one day to get the inclusive end date
    endDate = dayjs(endDate).subtract(1, 'day').toDate();
  }

  // Extract the repeat days
  const days = extractRepeatDays(vevent);

  return {
    startDate: dayjs(startDate).format('YYYY-MM-DD'),
    endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
    days,
    recurring: true,
    fullDays: true,
  };
};
